import { cn } from "@/lib/utils";
import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange as RDPDateRange } from "react-day-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/shared/ui/popover";
import { Calendar } from "@/app/shared/ui/calendar";
import { Presets } from "./presets";
import {
  DatePickerContext,
  formatDateRange,
  getDefaultPresets,
  isSameDay,
  startOfDay,
  endOfDay,
} from "./shared";
import { Trigger } from "./trigger";
import type { DateRange, DateRangePreset, PickerProps } from "./types";

type DateRangePickerProps = {
  presets?: DateRangePreset[];
  presetId?: DateRangePreset["id"];
  defaultValue?: DateRange;
  value?: DateRange;
  onChange?: (dateRange?: DateRange, preset?: DateRangePreset) => void;
} & PickerProps;

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);

  return matches;
}

function useScrollProgress(ref: React.RefObject<HTMLDivElement | null>) {
  const [scrollProgress, setScrollProgress] = useState(0);

  const updateScrollProgress = () => {
    if (!ref.current) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) {
      setScrollProgress(1);
      return;
    }
    setScrollProgress(scrollTop / maxScroll);
  };

  return { scrollProgress, updateScrollProgress };
}

function PresetScrollContainer({ children }: PropsWithChildren) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollProgress, updateScrollProgress } = useScrollProgress(ref);
  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={updateScrollProgress}
        className={cn(
          "flex h-14 w-full items-center sm:h-[300px] sm:w-44",
          "border-b border-border sm:border-b-0 sm:border-l",
          "overflow-y-auto scrollbar-hide",
        )}
      >
        {children}
      </div>
      <div
        className="pointer-events-none absolute bottom-0 left-0 hidden h-12 w-full bg-gradient-to-t from-white sm:block"
        style={{ opacity: 1 - scrollProgress ** 2 }}
      />
    </div>
  );
}

export function DateRangePicker({
  value,
  defaultValue,
  presetId,
  onChange,
  presets: customPresets,
  disabled,
  placeholder = "Select date range",
  hasError,
  align = "center",
  className,
  ...props
}: DateRangePickerProps) {
  const isDesktop = useMediaQuery("(min-width: 640px)");
  // Memoize: getDefaultPresets() builds a new array every call — without this
  // the sync effects below re-run on every render and clobber an in-progress
  // range-selection gesture.
  const presets = useMemo(() => customPresets ?? getDefaultPresets(), [customPresets]);

  const [open, setOpen] = useState(false);
  // Tracks a fresh range-selection gesture. While the calendar shows a
  // *complete* pre-selected range, react-day-picker's first click shrinks that
  // range (producing a complete range immediately) instead of starting a new
  // one — so the first click of a gesture is intercepted to start a fresh
  // partial range, and the second click completes it.
  const [isSelecting, setIsSelecting] = useState(false);
  const [preset, setPreset] = useState<DateRangePreset | undefined>(
    presets && presetId ? presets.find(({ id }) => id === presetId) : undefined,
  );
  const [range, setRange] = useState<DateRange | undefined>(
    preset?.dateRange ?? value ?? defaultValue ?? undefined,
  );
  const [month, setMonth] = useState<Date | undefined>(range?.to);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally capture range only when open changes
  const initialRange = useMemo(() => {
    return range;
  }, [open]);

  // Sync from the controlled value only when the actual dates change — the
  // parent passes an inline object whose identity changes every render, and
  // syncing on identity would clobber an in-progress selection gesture.
  const valueFrom = value?.from?.getTime();
  const valueTo = value?.to?.getTime();

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync on date values, not object identity
  useEffect(() => {
    setRange(value);
  }, [valueFrom, valueTo]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync on date values, not object identity
  useEffect(() => {
    const p = presets.find(({ id }) => id === presetId);
    setPreset(p);
    setRange(p?.dateRange ?? value ?? defaultValue);
  }, [presets, presetId, valueFrom, valueTo, defaultValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only update month when open state changes
  useEffect(() => {
    if (!open) setMonth(range?.to);
    else if (range) setMonth(range.to);
  }, [open]);

  const onCalendarSelect = (selectedRange: RDPDateRange | undefined, selectedDay: Date) => {
    // First click of a new gesture: ignore the range RDP derived from the
    // previous complete selection and anchor a fresh partial range instead.
    // The popover stays open for the second click.
    if (!isSelecting) {
      setIsSelecting(true);
      setPreset(undefined);
      setRange({ from: selectedDay, to: undefined });
      return;
    }
    setIsSelecting(false);

    let newRange: DateRange = {
      from: selectedRange?.from ?? selectedDay,
      to: selectedRange?.to ?? selectedDay,
    };

    if (newRange.from && newRange.to && isSameDay(newRange.from, newRange.to)) {
      newRange = {
        from: startOfDay(newRange.from),
        to: endOfDay(newRange.to),
      };
    }

    setRange(newRange);
    setPreset(undefined);
    if (newRange.from && newRange.to) {
      onChange?.(newRange);
      setOpen(false);
    }
  };

  const onPresetSelected = (selectedPreset: DateRangePreset) => {
    setIsSelecting(false);
    let adjustedDateRange = selectedPreset.dateRange;

    if (
      adjustedDateRange?.from &&
      adjustedDateRange?.to &&
      isSameDay(adjustedDateRange.from, adjustedDateRange.to)
    ) {
      adjustedDateRange = {
        from: startOfDay(adjustedDateRange.from),
        to: endOfDay(adjustedDateRange.to),
      };
    }

    setRange(adjustedDateRange);
    setPreset(selectedPreset);
    onChange?.(adjustedDateRange, selectedPreset);
    setOpen(false);
  };

  const onCancel = () => {
    setRange(initialRange);
    setOpen(false);
  };

  const onOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onCancel();
      setIsSelecting(false);
    }
    setOpen(newOpen);
  };

  const displayRange = useMemo(() => formatDateRange(range), [range]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onPresetSelected is stable and adding it causes infinite loops
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const shortcutPreset = presets.find((p) => p.shortcut?.toLowerCase() === e.key.toLowerCase());
      if (shortcutPreset) {
        e.preventDefault();
        onPresetSelected(shortcutPreset);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, presets]);

  return (
    <DatePickerContext.Provider value={{ isOpen: open, setIsOpen: setOpen }}>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Trigger
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            hasError={hasError}
            aria-required={props["aria-required"]}
            aria-invalid={props["aria-invalid"]}
            aria-label={props["aria-label"]}
            aria-labelledby={props["aria-labelledby"]}
            data-state={open ? "open" : "closed"}
          >
            {preset?.label ?? displayRange}
          </Trigger>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-auto p-0 rounded-xl" sideOffset={8}>
          <div className="flex">
            <div className="relative flex flex-col sm:flex-row-reverse sm:items-stretch">
              {presets && presets.length > 0 && (
                <PresetScrollContainer>
                  <div className="px-3 py-2 sm:py-3">
                    <Presets
                      currentPresetId={presetId}
                      currentValue={range}
                      presets={presets}
                      onSelect={onPresetSelected}
                    />
                  </div>
                </PresetScrollContainer>
              )}
              <div>
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={onCalendarSelect}
                  month={month}
                  onMonthChange={setMonth}
                  numberOfMonths={isDesktop ? 2 : 1}
                  classNames={{
                    months: "flex flex-row divide-x divide-border",
                  }}
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </DatePickerContext.Provider>
  );
}
